/**
 * ProductSearchPicker — searchable combobox for product catalog selection.
 *
 * Near-mechanical clone of `ContractorSearchPicker` retargeted to the products
 * catalog (CRM-core `products` entity). Used by document-line editors to
 * optionally link a line to a catalog product (a document line can also be pure
 * text — this picker is optional, never a hard requirement).
 *
 * Two-step fetch on selection:
 *  1. Dropdown list — GET /products?q=<search>&companyId=<companyId>&limit=30.
 *     The LIST row does NOT include `pkwiu`/`cn` (not selected by the endpoint).
 *  2. On pick — GET /products/:id (full row, incl. pkwiu/cn) to complete the
 *     `ProductPickResult` before calling `onSelect`. If the detail fetch fails,
 *     falls back to the list row's fields with `pkwiu: null, cn: null` rather
 *     than blocking the selection.
 *
 * Graceful degradation (deviation from the contractor template): the products
 * endpoint is gated behind the `crm_core` module and can return
 * 402/403 for tenants without it, or an empty catalog. The list queryFn
 * treats ONLY 402/403 as the graceful case (resolves to an empty list, so the
 * dropdown renders a friendly "no products" state) — any OTHER error (network,
 * 500, etc.) is rethrown and surfaces as a distinct `isError` state in the
 * dropdown, so a real backend failure is never indistinguishable from "tenant
 * has no products". The detail fetch on selection stays catch-all (see
 * `select()`) — selection must not block on it regardless of error shape.
 *
 * Props (UNCHANGED across the dropdown redesign below):
 *  value         — currently selected product id (null = none)
 *  selectedLabel — product name to display when closed (parent's cached label)
 *  companyId     — explicit `?companyId=` for GET /products (Model B listing,
 *                  see `resolveListCompanyScope`); undefined/null = unscoped.
 *  disabled      — disables the trigger
 *  onSelect      — called with the picked ProductPickResult, or null when cleared
 *  aria-label    — optional accessible name of the trigger (combobox)
 *  data-testid   — forwarded to root div; -search, -search-clear, -more and
 *                  -option-<id> suffixes added
 *
 * Dropdown results render as a compact table (Kod | Nazwa | jm | Cena) instead
 * of a flat list — same two-step fetch/select behaviour, same `-option-<id>`
 * testid per row. The search input has an inline clear (X) button. The trigger
 * also carries a "…" button that opens the advanced picker modal
 * (`ProductPickerModal`, F2) via `onOpenAdvancedPicker` — disabled when the
 * prop is not supplied (callers that don't wire it keep the old no-op trigger).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2, MoreHorizontal, Search, X } from 'lucide-react';

import { CellPopover } from '@/components/additional/CellPopover';
import { api } from '@/lib/api';
import { isModuleGatingError, MODULE_GATED_LIST_STALE_TIME_MS } from '@/lib/module-gating';

export interface ProductPickResult {
  id: string;
  code: string | null;
  name: string;
  unit: string | null;
  basePrice: string | null; // sell price (products.base_price), decimal string
  vatRate: string | null; // Symfonia-style, e.g. "23%" (products.vat_rate)
  pkwiu: string | null;
  cn: string | null;
}

/** LIST row shape — GET /products does not select pkwiu/cn. */
interface ProductListRow {
  id: string;
  code: string | null;
  name: string;
  unit: string | null;
  basePrice: string | null;
  vatRate: string | null;
}

interface ProductsListResponse {
  products: ProductListRow[];
  limit: number;
  offset: number;
}

/** DETAIL row shape — GET /products/:id full row; superset consumed as ProductPickResult. */
interface ProductDetailResponse {
  product: ProductPickResult;
}

/** Debounce delay (ms) between keystrokes in the search box and the actual GET /products
 * request — avoids firing one request per typed character. */
const SEARCH_DEBOUNCE_MS = 250;

export interface ProductSearchPickerProps {
  value: string | null;
  selectedLabel?: string | null;
  companyId?: string | null;
  disabled?: boolean;
  onSelect: (product: ProductPickResult | null) => void;
  /** Otwiera zaawansowany picker (`ProductPickerModal`, F2) — „…" w triggerze jest
   * disabled/no-op gdy nie podano (caller nie wpiął jeszcze modala). */
  onOpenAdvancedPicker?: () => void;
  /** Nazwa dostępna triggera (combobox) — np. podpis kolumny, gdy picker stoi w komórce tabeli bez
   * widocznej etykiety. Brak = nazwa z zawartości triggera (wybrany towar albo placeholder). */
  'aria-label'?: string;
  'data-testid'?: string;
}

export function ProductSearchPicker({
  value,
  selectedLabel,
  companyId,
  disabled = false,
  onSelect,
  onOpenAdvancedPicker,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: ProductSearchPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // `inputValue` — immediate, bound to the <input> so typing never feels laggy.
  // `search` — debounced (SEARCH_DEBOUNCE_MS) copy that actually feeds the query
  // key, so a GET /products fires once per pause in typing, not once per keystroke.
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(inputValue), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, isLoading, isError } = useQuery<ProductsListResponse>({
    queryKey: ['products-search', search, companyId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '30' });
      if (search) params.set('q', search);
      if (companyId) params.set('companyId', companyId);
      try {
        const res = await api.get<ProductsListResponse>(`/products?${params.toString()}`);
        return res.data;
      } catch (err) {
        // Moduł katalogu może być nieaktywny (402) albo brak uprawnienia
        // (403) — degradujemy do pustej listy zamiast wywalać dropdown. INNE błędy
        // (sieć, 500) rzucamy dalej — mają wpaść w isError, nie mieszać się z
        // "tenant nie ma towarów".
        if (isModuleGatingError(err)) {
          return { products: [], limit: 30, offset: 0 };
        }
        throw err;
      }
    },
    enabled: isOpen,
    staleTime: MODULE_GATED_LIST_STALE_TIME_MS,
  });

  const productRows = data?.products ?? [];

  function open() {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => searchRef.current?.focus(), 50);
  }

  function close() {
    setIsOpen(false);
    setInputValue('');
    setSearch('');
  }

  async function select(row: ProductListRow) {
    if (pendingId) return;
    setPendingId(row.id);
    try {
      const res = await api.get<ProductDetailResponse>(`/products/${row.id}`);
      onSelect(res.data.product);
    } catch {
      // Detail fetch nie blokuje wyboru — fallback do pól z wiersza listy
      // (pkwiu/cn niedostępne w LIST, więc null). Catch-all CELOWO (inaczej niż
      // lista): użytkownik już kliknął konkretny wiersz, więc niezależnie od
      // kształtu błędu (402/403 gating albo realna awaria 500) selekcja ma się
      // domknąć — nie blokujemy jej ani nie odróżniamy stanu błędu na tym etapie.
      onSelect({
        id: row.id,
        code: row.code,
        name: row.name,
        unit: row.unit,
        basePrice: row.basePrice,
        vatRate: row.vatRate,
        pkwiu: null,
        cn: null,
      });
    } finally {
      setPendingId(null);
      close();
    }
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onSelect(null);
  }

  const triggerBase =
    'flex w-full cursor-pointer items-center gap-1.5 rounded border px-2 py-1 text-sm transition bg-bg-primary';

  const triggerState = disabled
    ? 'cursor-not-allowed opacity-50 border-border-primary text-text-muted'
    : 'border-border-primary text-text-primary hover:border-accent-400 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500';

  const hasValue = value !== null;
  // Etykieta zamkniętego pickera: nazwa z cache (selectedLabel); gdy pusta — skrócony
  // id (jak ContractorSearchPicker), nie surowy UUID.
  const displayLabel = selectedLabel?.trim() || (value ? value.slice(0, 8) + '…' : '');

  return (
    <div className="relative" data-testid={testId}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        tabIndex={disabled ? -1 : 0}
        onClick={open}
        onKeyDown={(e) => e.key === 'Enter' && open()}
        className={`${triggerBase} ${triggerState}`}
      >
        <Search className="h-4 w-4 flex-shrink-0 text-text-muted" />
        {hasValue && displayLabel ? (
          <span className="flex-1 truncate">{displayLabel}</span>
        ) : (
          <span className="flex-1 truncate text-text-muted">
            {t('ui.productPicker.placeholder')}
          </span>
        )}
        {hasValue && !disabled && (
          <button
            type="button"
            onClick={clear}
            aria-label={t('ui.productPicker.clearAriaLabel')}
            className="flex-shrink-0 hover:text-danger-600"
            tabIndex={-1}
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
        {/* Otwiera ProductPickerModal (F2) przez onOpenAdvancedPicker — disabled/no-op gdy
          caller nie podał propa (stary konsument bez modala). stopPropagation zawsze:
          disabled nie gwarantuje zablokowania bubblingu syntetycznego clicku (np.
          dispatchEvent) do triggera-rodzica z onClick={open}, a gdy WŁĄCZONY, klik ma
          otworzyć modal, nie dropdown pod spodem. */}
        <button
          type="button"
          disabled={disabled || !onOpenAdvancedPicker}
          onClick={(e) => {
            e.stopPropagation();
            onOpenAdvancedPicker?.();
          }}
          aria-label={t('ui.productPicker.moreTooltip')}
          title={t('ui.productPicker.moreTooltip')}
          className={
            disabled || !onOpenAdvancedPicker
              ? 'flex-shrink-0 cursor-not-allowed text-text-muted opacity-50'
              : 'flex-shrink-0 text-text-muted hover:text-accent-600'
          }
          tabIndex={-1}
          data-testid={testId ? `${testId}-more` : undefined}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </div>

      {/* Dropdown w portalu (CellPopover) — ucieka z overflow-x tabeli, który przycinał go w pionie. */}
      <CellPopover
        open={isOpen}
        anchorRef={triggerRef as React.RefObject<HTMLElement | null>}
        onClose={close}
        ariaLabel={t('ui.productPicker.placeholder')}
        className="w-80 overflow-hidden"
        data-testid={testId ? `${testId}-dropdown` : undefined}
      >
        {/* Search input */}
        <div className="border-b border-border-primary p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-text-muted" />
            <input
              ref={searchRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  close();
                }
              }}
              placeholder={t('ui.productPicker.searchPlaceholder')}
              className="w-full rounded-md border border-border-primary bg-bg-primary py-1.5 pl-8 pr-8 text-sm text-text-primary placeholder-text-muted focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            {inputValue && (
              <button
                type="button"
                onClick={() => {
                  setInputValue('');
                  searchRef.current?.focus();
                }}
                aria-label={t('ui.productPicker.clearSearchAriaLabel')}
                className="absolute right-2 top-2 text-text-muted hover:text-danger-600"
                data-testid={testId ? `${testId}-search-clear` : undefined}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Results list */}
        <div className="max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
              <span className="ml-2 text-sm text-text-muted">{t('ui.productPicker.loading')}</span>
            </div>
          ) : isError ? (
            <p
              className="p-3 text-center text-sm text-danger-600"
              data-testid={testId ? `${testId}-error` : undefined}
            >
              {t('ui.productPicker.loadError')}
            </p>
          ) : productRows.length === 0 ? (
            <p className="p-3 text-center text-sm text-text-muted">
              {t('ui.productPicker.noResults')}
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border-primary bg-bg-secondary text-text-secondary">
                  <th className="px-2 py-1.5 text-left font-medium" scope="col">
                    {t('ui.productPicker.columns.code')}
                  </th>
                  <th className="px-2 py-1.5 text-left font-medium" scope="col">
                    {t('ui.productPicker.columns.name')}
                  </th>
                  <th className="px-2 py-1.5 text-left font-medium" scope="col">
                    {t('ui.productPicker.columns.unit')}
                  </th>
                  <th className="px-2 py-1.5 text-right font-medium" scope="col">
                    {t('ui.productPicker.columns.price')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {productRows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => void select(row)}
                    className={[
                      'cursor-pointer transition',
                      value === row.id
                        ? 'bg-accent-50 text-accent-700'
                        : 'text-text-primary hover:bg-bg-secondary',
                      pendingId === row.id ? 'pointer-events-none opacity-60' : '',
                    ].join(' ')}
                    data-testid={testId ? `${testId}-option-${row.id}` : undefined}
                  >
                    <td className="px-2 py-1.5">{row.code ?? '—'}</td>
                    <td className="px-2 py-1.5">
                      <span className="flex items-center gap-1.5">
                        {row.name}
                        {pendingId === row.id && (
                          <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin text-text-muted" />
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-1.5">{row.unit ?? '—'}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{row.basePrice ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CellPopover>
    </div>
  );
}
